import { Grid } from "@material-ui/core";
import { useContext, useEffect, useMemo } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Observable } from "rxjs";
import { ErrorText } from "../../../../../../../components/billing";
import { FileField } from "../../../../../../../components/forms/file-3";
import { RadioGroup } from "../../../../../../../components/forms/radio";
import { GridItem } from "../../../../../../../components/layout";
import {
  IdentityAttachmentType,
  useIdentityAttachmentType,
} from "../../../../../../../store/common/models";
import { ColumnWrapper } from "../../../../../../../store/launch-configurations/models/default-fields/common";
import {
  IllustrationMap,
  Instructions,
  List,
  methodsWithTwoSides,
} from "../../../../../../../store/launch-configurations/models/default-fields/identity-document/attachment";
import { mapObject } from "../../../../../../../utils/utils";
import { FileAttachmentContext } from "./file-attachment-context";
import { IdTypeContext } from "./id-type-context";
import { Container } from "../../components";
import { useFileUploader } from "./file-upload-hook";

export interface IProofofIdentitityProps {
  emailChange: Observable<string>;
  setFilesUploaded: (b: boolean) => void;
}

export function ProofofIdentity({
  emailChange,
  setFilesUploaded,
}: IProofofIdentitityProps) {
  const { t } = useTranslation("fields");
  const {
    formState: { isSubmitted: isParentFormSubmitted },
  } = useFormContext();

  const { files, setFile, emailAddress } = useContext(FileAttachmentContext);
  const { idType, setIdType } = useContext(IdTypeContext);

  const [idDocSubject, idDocUploaded, idDocSuccess] = useFileUploader(
    "IDENTIFICATION_DOCUMENT",
    emailChange
  );

  const [
    additionalIdDocSubject,
    additionalIdDocUploaded,
    additionalIdSuccess,
  ] = useFileUploader("ADDITIONAL_IDENTIFICATION_DOCUMENT", emailChange);

  // Re-uploading the file if email has changed since last (to run only on mount, once)
  useEffect(() => {
    if (
      files?.IDENTIFICATION_DOCUMENT?.emailAddress &&
      files?.IDENTIFICATION_DOCUMENT?.emailAddress !== emailAddress
    ) {
      idDocSubject.next(files?.IDENTIFICATION_DOCUMENT);
    }
    if (
      idType &&
      methodsWithTwoSides.includes(idType) &&
      files?.ADDITIONAL_IDENTIFICATION_DOCUMENT?.file &&
      files?.ADDITIONAL_IDENTIFICATION_DOCUMENT?.emailAddress &&
      files?.ADDITIONAL_IDENTIFICATION_DOCUMENT?.emailAddress !== emailAddress
    ) {
      additionalIdDocSubject.next(files?.ADDITIONAL_IDENTIFICATION_DOCUMENT);
    }
  }, []);

  useEffect(() => {
    const sub = idDocSuccess.subscribe((d) => {
      setFile("IDENTIFICATION_DOCUMENT", d);
    });
    return () => sub.unsubscribe();
  }, [idDocSuccess, setFile]);

  useEffect(() => {
    const sub = additionalIdSuccess.subscribe((d) => {
      setFile("ADDITIONAL_IDENTIFICATION_DOCUMENT", d);
    });
    return () => sub.unsubscribe();
  }, [additionalIdSuccess, setFile]);

  const idError = useMemo(
    () =>
      !idDocUploaded && isParentFormSubmitted && !files.IDENTIFICATION_DOCUMENT
        ? new Error("Field is required")
        : undefined,
    [idDocUploaded, isParentFormSubmitted]
  );

  const additionalIdError = useMemo(
    () =>
      !additionalIdDocUploaded &&
      isParentFormSubmitted &&
      idType &&
      methodsWithTwoSides.includes(idType) &&
      !files.ADDITIONAL_IDENTIFICATION_DOCUMENT
        ? new Error("Field is required")
        : undefined,
    [
      additionalIdDocUploaded,
      isParentFormSubmitted,
      methodsWithTwoSides,
      idType,
    ]
  );

  const {
    watch,
    formState: { isSubmitted },
    trigger,
    control,
  } = useForm<{
    file: File;
    additionalFile: File;
    type: IdentityAttachmentType;
  }>({
    defaultValues: {
      type: idType,
      file: files?.IDENTIFICATION_DOCUMENT?.file,
      additionalFile: files?.ADDITIONAL_IDENTIFICATION_DOCUMENT?.file,
    },
  });

  useEffect(() => {
    if (isParentFormSubmitted) trigger();
  }, [trigger, isParentFormSubmitted]);

  useEffect(() => {
    const sub = watch((data, { name }) => {
      if (name === "additionalFile" || name === "type") {
        additionalIdDocSubject.next({
          file: data.additionalFile,
          type: data.type,
        });
      }
      if (name == "file" || name === "type") {
        idDocSubject.next({
          file: data.file,
          type: data.type,
        });
      }
    });

    return () => sub.unsubscribe();
  }, [
    watch,
    isParentFormSubmitted,
    trigger,
    isSubmitted,
    additionalIdDocSubject,
    idDocSubject,
  ]);

  const IdentityAttachmentType = useIdentityAttachmentType();
  const optionTypes = useMemo(
    () =>
      mapObject(IdentityAttachmentType.enum, (val, key) => ({
        label: val,
        id: key,
      })),
    [IdentityAttachmentType]
  );

  const type = watch("type");
  useEffect(() => {
    setIdType(type);
  }, [type, setIdType]);

  const hasTwoSides = useMemo(() => methodsWithTwoSides.includes(type), [type]);

  useEffect(() => {
    if (
      (idDocUploaded || files.IDENTIFICATION_DOCUMENT) &&
      type &&
      (!methodsWithTwoSides.includes(type) ||
        additionalIdDocUploaded ||
        files.ADDITIONAL_IDENTIFICATION_DOCUMENT)
    ) {
      setFilesUploaded(true);
    } else {
      setFilesUploaded(false);
    }
  }, [
    setFilesUploaded,
    idDocUploaded,
    type,
    methodsWithTwoSides,
    additionalIdDocUploaded,
    files,
  ]);

  return (
    <Container>
      <Grid container spacing={3}>
        <GridItem top xs={12}>
          <strong>{t("proof-of-identity")}</strong>
        </GridItem>
        <GridItem top xs={12}>
          {t("choose-your-document-type")}
        </GridItem>
        <GridItem top xs={12} md={6} column>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <RadioGroup<IdentityAttachmentType>
                fullWidth
                options={optionTypes}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
          <Instructions>
            {t("take-a-photo-of-your", {
              type: IdentityAttachmentType.enum[type],
            })}
          </Instructions>
          <List dangerouslySetInnerHTML={{ __html: t("photo-qualities") }} />
        </GridItem>
        <GridItem top xs={12} md={6}>
          <img src={IllustrationMap[type]} />
        </GridItem>
        <GridItem top xs={12}>
          {t("upload-your-document")}
        </GridItem>
        <GridItem top xs={12} sm={12} md={hasTwoSides ? 6 : 12}>
          <ColumnWrapper>
            <Controller
              control={control}
              name="file"
              render={({ field, fieldState }) => (
                <FileField
                  onChange={field.onChange}
                  value={field.value}
                  error={!!fieldState.error}
                  label={t(hasTwoSides ? "front-side" : "identity-document")}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            {idError ? <ErrorText>{idError.message}</ErrorText> : null}
          </ColumnWrapper>
        </GridItem>
        {hasTwoSides ? (
          <GridItem top item xs={12} sm={12} md={6}>
            <ColumnWrapper>
              <Controller
                control={control}
                name="additionalFile"
                render={({ field, fieldState }) => (
                  <FileField
                    onChange={field.onChange}
                    value={field.value}
                    error={!!fieldState.error}
                    label={t("back-side")}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              {additionalIdError ? (
                <ErrorText>{additionalIdError.message}</ErrorText>
              ) : null}
            </ColumnWrapper>
          </GridItem>
        ) : null}
      </Grid>
    </Container>
  );
}
